import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement } from 'chart.js';
import api from '../utils/api';
import { fmt } from '../utils/gst';
import { getInitialMonth } from '../utils/format';
import SearchableSelect from '../components/SearchableSelect';
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement);

const COLORS = ['#2D6BE4','#6D28D9','#1E7E4E','#D97706','#C0392B','#0369A1','#9D174D','#065F46'];

export default function VisualsPage() {
  const [month, setMonth] = useState(getInitialMonth);
  const [clientFilter, setClientFilter] = useState('');
  const [personFilter, setPersonFilter] = useState('');

  useEffect(()=>{const h=e=>setMonth(e.detail);window.addEventListener('monthChange',h);return()=>window.removeEventListener('monthChange',h);},[]);

  const { data: allEntries=[] } = useQuery({ queryKey:['entries',month], queryFn:()=>api.get(`/entries?month=${month}`).then(r=>r.data) });
  const { data: clients=[] } = useQuery({ queryKey:['clients'], queryFn:()=>api.get('/clients').then(r=>r.data) });

  const shownClients = clientFilter ? clients.filter(c=>c.id===clientFilter) : clients;
  const persons = clientFilter ? [...new Set(allEntries.filter(e=>e.client_id===clientFilter&&e.parent_node_name).map(e=>e.parent_node_name))] : [];

  return (
    <div>
      <div className="section-head">
        <h2>Client visuals</h2>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <div style={{width:170}}>
            <SearchableSelect value={clientFilter} onChange={id=>{setClientFilter(id);setPersonFilter('');}}
              items={clients} allowClear clearLabel="All clients" placeholder="All clients" />
          </div>
          {persons.length>0&&<select value={personFilter} onChange={e=>setPersonFilter(e.target.value)} style={{fontSize:12}}>
            <option value="">All persons</option>
            {persons.map(p=><option key={p} value={p}>{p}</option>)}
          </select>}
        </div>
      </div>

      {shownClients.map(c=>{
        let cl = allEntries.filter(e=>e.client_id===c.id);
        if (personFilter) cl = cl.filter(e=>e.parent_node_name===personFilter);
        if (!cl.length) return null;

        const catJobs={}, catRev={}, nodeJobs={}, shJobs={};
        cl.forEach(e=>{
          catJobs[e.category_name]=(catJobs[e.category_name]||0)+e.quantity;
          catRev[e.category_name]=(catRev[e.category_name]||0)+(parseFloat(e.unit_price||0)*e.quantity);
          if(e.parent_node_name){nodeJobs[e.parent_node_name]=(nodeJobs[e.parent_node_name]||0)+e.quantity;}
          if(e.node_name&&e.parent_node_name){const k=`${e.parent_node_name}/${e.node_name}`;shJobs[k]=(shJobs[k]||0)+e.quantity;}
        });

        // daily trend within month
        const dayMap={};
        cl.forEach(e=>{dayMap[e.work_date]=(dayMap[e.work_date]||0)+e.quantity;});
        const sortedDays=Object.keys(dayMap).sort();

        const totalJobs=cl.reduce((s,e)=>s+e.quantity,0);
        const totalRev=cl.reduce((s,e)=>s+(parseFloat(e.unit_price||0)*e.quantity),0);
        const corrCount=cl.filter(e=>e.corrections>0).length;

        return (
          <div key={c.id} className="card" style={{marginBottom:24}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:8}}>
              <div style={{fontWeight:800,fontSize:16}}>{c.name}</div>
              <div style={{display:'flex',gap:12}}>
                <div style={{textAlign:'center'}}><div style={{fontSize:11,color:'var(--text2)'}}>Total Jobs</div><div style={{fontWeight:700,fontSize:18}}>{totalJobs}</div></div>
                {totalRev>0&&<div style={{textAlign:'center'}}><div style={{fontSize:11,color:'var(--text2)'}}>Base Revenue</div><div style={{fontWeight:700,fontSize:18,color:'var(--accent)'}}>{fmt(totalRev)}</div></div>}
                <div style={{textAlign:'center'}}><div style={{fontSize:11,color:'var(--text2)'}}>Corrections</div><div style={{fontWeight:700,fontSize:18,color:corrCount>0?'var(--red)':undefined}}>{corrCount}</div></div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
              <div className="card" style={{border:'1px solid var(--border)'}}>
                <div className="card-title">Jobs by category</div>
                <div style={{height:180}}><Doughnut data={{labels:Object.keys(catJobs),datasets:[{data:Object.values(catJobs),backgroundColor:COLORS,borderWidth:0}]}} options={{responsive:true,maintainAspectRatio:false,cutout:'60%',plugins:{legend:{position:'right',labels:{font:{size:10},boxWidth:8}}}}}/></div>
              </div>
              <div className="card" style={{border:'1px solid var(--border)'}}>
                <div className="card-title">Revenue by category</div>
                <div style={{height:180}}><Bar data={{labels:Object.keys(catRev),datasets:[{data:Object.values(catRev),backgroundColor:COLORS,borderRadius:4}]}} options={{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false}},scales:{x:{grid:{display:false},ticks:{font:{size:9}}},y:{ticks:{callback:v=>'₹'+v.toLocaleString('en-IN'),font:{size:9}},border:{display:false}}}}}/></div>
              </div>
              {Object.keys(nodeJobs).length>0&&<div className="card" style={{border:'1px solid var(--border)'}}>
                <div className="card-title">Jobs by person</div>
                <div style={{height:180}}><Bar data={{labels:Object.keys(nodeJobs),datasets:[{data:Object.values(nodeJobs),backgroundColor:COLORS,borderRadius:4}]}} options={{responsive:true,maintainAspectRatio:false,indexAxis:'y',plugins:{legend:{display:false}},scales:{x:{grid:{color:'rgba(0,0,0,.05)'},ticks:{font:{size:9}}},y:{grid:{display:false},ticks:{font:{size:10}}}}}}/></div>
              </div>}
              {Object.keys(shJobs).length>0&&<div className="card" style={{border:'1px solid var(--border)'}}>
                <div className="card-title">Jobs by stakeholder</div>
                <div style={{height:180}}><Doughnut data={{labels:Object.keys(shJobs),datasets:[{data:Object.values(shJobs),backgroundColor:COLORS,borderWidth:0}]}} options={{responsive:true,maintainAspectRatio:false,cutout:'55%',plugins:{legend:{position:'right',labels:{font:{size:9},boxWidth:7}}}}}/></div>
              </div>}
            </div>

            {sortedDays.length>1&&<div className="card" style={{border:'1px solid var(--border)'}}>
              <div className="card-title">Daily work trend</div>
              <div style={{height:150}}><Line data={{labels:sortedDays,datasets:[{label:'Jobs',data:sortedDays.map(d=>dayMap[d]),borderColor:'#2D6BE4',backgroundColor:'rgba(45,107,228,.1)',tension:.3,fill:true,pointRadius:3}]}} options={{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false}},scales:{x:{grid:{display:false},ticks:{font:{size:9}}},y:{ticks:{font:{size:9}},border:{display:false}}}}}/></div>
            </div>}
          </div>
        );
      })}
      {!shownClients.some(c=>allEntries.some(e=>e.client_id===c.id))&&<div className="empty">No data for this period</div>}
    </div>
  );
}
